import React, { useMemo, useEffect, useState } from "react";
import { Button } from "@shared/ui/button";
import { ChevronRight, ChevronLeft, HelpCircle } from "lucide-react";
import { StepHelpers } from "@shared/components/Stepper";
import { Badge } from "@shared/ui/badge";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import {
  useChannelChoices,
  useNoticeTemplates,
} from "@features/cx/outstanding/hooks";


interface ReminderDetailsProps {
  data?: {
    communicationChannel?: string;
    reminderTemplate?: string | number;
    templateName?: string;
  };
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
}

export const ReminderDetails: React.FC<ReminderDetailsProps> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  stepHelpers,
  currentStepIndex = 1,
}) => {
  const [selectedChannelType, setSelectedChannelType] = useState<string>("");
  const { data: channelList } = useChannelChoices("quick_messaging");
  const { data: templateList } = useNoticeTemplates(selectedChannelType);

  const channelChoices = useMemo(
    () =>
      channelList?.map((data: any) => ({
        value: data.key,
        label: data.value,
      })) || [],
    [channelList],
  );

  const templateChoices = useMemo(() => {
    return (
      templateList?.map((temp: any) => ({
        label: temp.templateName,
        value: temp.id,
      })) || []
    );
  }, [templateList]);

  // Load persisted step data if any
  const savedData = (() => {
    if (!stepHelpers) return null;
    try {
      return stepHelpers.getStepData<{
        communicationChannel?: string;
        reminderTemplate?: string | number;
        templateName?: string;
      }>(currentStepIndex);
    } catch {
      return null;
    }
  })();

  const defaultValues = {
    communicationChannel:
      savedData?.communicationChannel || data?.communicationChannel || "",
    // Always string so GenericSelect matches String(opt.value)
    reminderTemplate:
      savedData?.reminderTemplate != null && savedData.reminderTemplate !== ""
        ? String(savedData.reminderTemplate)
        : data?.reminderTemplate != null && data.reminderTemplate !== ""
        ? String(data.reminderTemplate)
        : "",
  };

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "communicationChannel",
        label: "Communication Channel",
        type: "select",
        required: true,
        placeholder: "Select Channel",
        options: channelChoices,
        className: "col-span-12",
      },
      {
        name: "reminderTemplate",
        label: "Reminder Template",
        type: "select",
        required: true,
        placeholder: "Select Template",
        options: templateChoices,
        className: "col-span-12",
      },
    ],
    [channelChoices, templateChoices],
  );

  const reminderForm = FormService.createForm<any>({
    fields: formFields,
    mode: "onChange",
    defaultValues,
  });

  useEffect(() => {
    // Reset form with default values on mount (covers navigation back)
    reminderForm.reset(defaultValues);
  }, []);

  const syncStateToStepper = (values: any) => {
    onDataChange?.(values);
    if (stepHelpers) {
      stepHelpers.setStepData(currentStepIndex, values);
    }
  };

  const handleFieldChange = (name: string, value: any) => {
    if (value === undefined || value === null) return;
    const currentValues = reminderForm.getValues();
    const currentData =
      stepHelpers?.getStepData<{
        communicationChannel?: string;
        reminderTemplate?: string | number;
        templateName?: string;
      }>(currentStepIndex) || {};
    const updated: any = { ...currentData, ...currentValues, [name]: value };

    if (name === "reminderTemplate" && templateList) {
      const selectedTemp = templateList.find(
        (temp: any) => String(temp.id) === String(value),
      );
      if (selectedTemp) {
        updated.reminderTemplate = Number(value);
        updated.templateName = selectedTemp.templateName;
      }
    }
    if (name === "communicationChannel") {
      const previousChannel = currentData.communicationChannel;
      if (previousChannel && String(previousChannel) !== String(value)) {
        updated.reminderTemplate = "";
        updated.templateName = "";
        reminderForm.setValue("reminderTemplate", "");
      }
    }

    syncStateToStepper(updated);
  };

  const currentTemplate = reminderForm.watch("reminderTemplate");
  const currentChannel = reminderForm.watch("communicationChannel");

  useEffect(() => {
    if (currentChannel !== undefined && currentChannel !== null) {
      const str = currentChannel.toString().toUpperCase();
      if (str === "0" || str === "EMAIL") {
        setSelectedChannelType("EMAIL");
      } else if (
        str === "1" ||
        str === "SMS" ||
        str === "TEXT MESSAGE" ||
        str === "TEXT_MESSAGE"
      ) {
        setSelectedChannelType("SMS");
      } else {
        setSelectedChannelType("");
      }
    }
  }, [currentChannel]);

  // When templateList resolves and a template is selected, always sync templateName to step data
  useEffect(() => {
    if (currentTemplate && templateList && templateList.length > 0) {
      const selectedTemp = templateList.find(
        (temp: any) => String(temp.id) === String(currentTemplate),
      );
      if (selectedTemp) {
        const currentData =
          stepHelpers?.getStepData<{ templateName?: string }>(currentStepIndex) || {};
        if (currentData.templateName !== selectedTemp.templateName) {
          const updated = {
            ...currentData,
            communicationChannel: currentChannel,
            reminderTemplate: Number(currentTemplate),
            templateName: selectedTemp.templateName,
          };
          syncStateToStepper(updated);
        }
      }
    }
  }, [
    currentTemplate,
    currentChannel,
    templateList,
    stepHelpers,
    currentStepIndex,
  ]);

  // Autopopulate template selection when templateList resolves (covers navigation back)
  useEffect(() => {
    if (templateList && templateList.length > 0) {
      const savedTemplate =
        stepHelpers?.getStepData<{ reminderTemplate?: string | number }>(
          currentStepIndex,
        )?.reminderTemplate ?? data?.reminderTemplate;
      if (savedTemplate !== undefined && savedTemplate !== null && savedTemplate !== "") {
        const savedStr = String(savedTemplate);
        const currentVal = reminderForm.getValues("reminderTemplate");
        if (String(currentVal) !== savedStr) {
          reminderForm.setValue("reminderTemplate", savedStr);
        }
      }
    }
  }, [templateList, reminderForm, stepHelpers, currentStepIndex, data]);


  const renderedPreview = useMemo(() => {
    if (!currentTemplate) return "";
    const selectedTemplateObj = templateList?.find(
      (temp: any) =>
        temp.id === Number(currentTemplate) || temp.id === currentTemplate,
    );
    return selectedTemplateObj?.content?.body || "";
  }, [currentTemplate, templateList]);

  const isValid = currentChannel !== "" && currentTemplate !== "";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-2 p-1">
        <HelpCircle size={18} className="text-gray-400" />
        <span className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">
          Reminder Details
        </span>
        <span className="text-xs text-neutral-400">
          Configure parameters for reminder dispatch
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Form Panel */}
        <div className="bg-white p-5 border border-gray-200 rounded-lg shadow-sm">
          <DynamicForm
            form={reminderForm}
            fields={formFields}
            gridColumns={12}
            config={{
              classes: FIELD_CLASSES,
              showSubmitButton: false,
            }}
            onFieldChange={handleFieldChange}
          />
        </div>

        {/* Right Preview Panel */}
        <div className="flex flex-col bg-gray-50 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
            <span className="text-xs font-black text-gray-500 uppercase tracking-widest">
              Template Preview
            </span>
            {currentTemplate && (
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200 rounded-full text-[10px]"
              >
                Live Preview
              </Badge>
            )}
          </div>
          <div className="flex-1 p-5 flex flex-col justify-center min-h-[300px]">
            {currentTemplate ? (
              <div
                className="bg-white border border-gray-150 rounded-lg p-5 shadow-sm font-mono text-xs text-neutral-700 whitespace-pre-wrap leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderedPreview }}
              />
            ) : (
              <div className="text-center text-gray-400 italic text-sm">
                Select a template to view live preview rendering.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-100">
        <Button
          onClick={onPrevious}
          className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 font-medium flex items-center gap-1.5 rounded-lg px-4 py-2"
        >
          <ChevronLeft size={16} />
          <span>Previous: Select Accounts</span>
        </Button>

        <Button
          onClick={onNext}
          disabled={!isValid}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center gap-1.5 rounded-lg px-4 py-2"
        >
          <span>Next: Review & Send</span>
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
};

export default ReminderDetails;
